import { makeAutoObservable, runInAction } from 'mobx';
import { tenantApi } from '../services/tenantApi';
import { Tenant, LLMConfig, NotificationConfig, MonitoringConfig } from '../types';
import { authStore } from './authStore';

class TenantStore {
  tenants: Tenant[] = [];
  selectedTenant: Tenant | null = null;
  selectedLLMConfig: LLMConfig | null = null;
  selectedNotificationConfig: NotificationConfig | null = null;
  selectedMonitoringConfig: MonitoringConfig | null = null;
  isLoading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async loadTenants(includeDeleted = false) {
    this.isLoading = true;
    this.error = null;
    try {
      const tenants = await tenantApi.listTenants(includeDeleted);
      runInAction(() => {
        this.tenants = tenants;
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load tenants';
        this.isLoading = false;
      });
    }
  }

  async loadTenant(id: string) {
    this.isLoading = true;
    this.error = null;
    try {
      const tenant = await tenantApi.getTenant(id);
      runInAction(() => {
        this.selectedTenant = tenant;
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load tenant';
        this.isLoading = false;
      });
    }
  }

  async createTenant(tenant: Partial<Tenant>) {
    this.isLoading = true;
    this.error = null;
    try {
      const newTenant = await tenantApi.createTenant(tenant);
      runInAction(() => {
        this.tenants.push(newTenant);
        this.isLoading = false;
      });
      return newTenant;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to create tenant';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async updateTenant(id: string, tenant: Partial<Tenant>) {
    this.isLoading = true;
    this.error = null;
    try {
      const updatedTenant = await tenantApi.updateTenant(id, tenant);
      runInAction(() => {
        const index = this.tenants.findIndex(t => t.id === id);
        if (index !== -1) {
          this.tenants[index] = updatedTenant;
        }
        if (this.selectedTenant?.id === id) {
          this.selectedTenant = updatedTenant;
        }
        this.isLoading = false;
      });
      return updatedTenant;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to update tenant';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async deleteTenant(id: string) {
    this.isLoading = true;
    this.error = null;
    try {
      await tenantApi.deleteTenant(id);
      runInAction(() => {
        this.tenants = this.tenants.filter(t => t.id !== id);
        if (this.selectedTenant?.id === id) {
          this.selectedTenant = null;
        }
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to delete tenant';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async loadLLMSettings(tenantId: string) {
    this.isLoading = true;
    this.error = null;
    try {
      const config = await tenantApi.getLLMSettings(tenantId);
      runInAction(() => {
        this.selectedLLMConfig = config;
        this.isLoading = false;
      });
      return config;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load LLM settings';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async updateLLMSettings(tenantId: string, config: LLMConfig) {
    this.isLoading = true;
    this.error = null;
    try {
      const updatedConfig = await tenantApi.updateLLMSettings(tenantId, config);
      runInAction(() => {
        this.selectedLLMConfig = updatedConfig;
        // Update the tenant's settings if it's loaded
        const tenant = this.tenants.find(t => t.id === tenantId);
        if (tenant) {
          if (!tenant.settings) {
            tenant.settings = {};
          }
          tenant.settings.llmConfig = updatedConfig;
        }
        if (this.selectedTenant?.id === tenantId) {
          if (!this.selectedTenant.settings) {
            this.selectedTenant.settings = {};
          }
          this.selectedTenant.settings.llmConfig = updatedConfig;
        }
        this.isLoading = false;
      });
      return updatedConfig;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to update LLM settings';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async loadNotificationSettings(tenantId: string) {
    this.isLoading = true;
    this.error = null;
    try {
      const config = await tenantApi.getNotificationSettings(tenantId);
      runInAction(() => {
        this.selectedNotificationConfig = config;
        this.isLoading = false;
      });
      return config;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load notification settings';
        this.isLoading = false;
      });
      // Return default config on error
      const defaultConfig: NotificationConfig = {
        callingField: 'calling',
        waitingField: 'waiting',
        roomField: 'room',
        nameField: 'name',
        notifyOnRegistration: true,
        notifyOnCalling: true,
        notifyOnWaiting: false,
      };
      return defaultConfig;
    }
  }

  async updateNotificationSettings(tenantId: string, config: NotificationConfig) {
    this.isLoading = true;
    this.error = null;
    try {
      const updatedConfig = await tenantApi.updateNotificationSettings(tenantId, config);
      runInAction(() => {
        this.selectedNotificationConfig = updatedConfig;
        // Update the tenant's settings if it's loaded
        const tenant = this.tenants.find(t => t.id === tenantId);
        if (tenant) {
          if (!tenant.settings) {
            tenant.settings = {};
          }
          tenant.settings.notificationConfig = updatedConfig;
        }
        if (this.selectedTenant?.id === tenantId) {
          if (!this.selectedTenant.settings) {
            this.selectedTenant.settings = {};
          }
          this.selectedTenant.settings.notificationConfig = updatedConfig;
        }
        this.isLoading = false;
      });
      return updatedConfig;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to update notification settings';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async loadMonitoringSettings(tenantId: string) {
    this.isLoading = true;
    this.error = null;
    try {
      const config = await tenantApi.getMonitoringSettings(tenantId);
      runInAction(() => {
        this.selectedMonitoringConfig = config;
        this.isLoading = false;
      });
      return config;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load monitoring settings';
        this.isLoading = false;
      });
      // Return default config on error
      const defaultConfig: MonitoringConfig = {
        screenUploadMonitoring: {
          enabled: false,
          slackWebhookUrl: '',
          noUploadCheck: false,
          thresholdMinutes: 5,
          monitoredScreenCount: 1,
        },
      };
      return defaultConfig;
    }
  }

  async updateMonitoringSettings(tenantId: string, config: MonitoringConfig) {
    this.isLoading = true;
    this.error = null;
    try {
      const updatedConfig = await tenantApi.updateMonitoringSettings(tenantId, config);
      runInAction(() => {
        this.selectedMonitoringConfig = updatedConfig;
        // Update the tenant's settings if it's loaded
        const tenant = this.tenants.find(t => t.id === tenantId);
        if (tenant) {
          if (!tenant.settings) {
            tenant.settings = {};
          }
          tenant.settings.monitoringConfig = updatedConfig;
        }
        if (this.selectedTenant?.id === tenantId) {
          if (!this.selectedTenant.settings) {
            this.selectedTenant.settings = {};
          }
          this.selectedTenant.settings.monitoringConfig = updatedConfig;
        }
        this.isLoading = false;
      });
      return updatedConfig;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to update monitoring settings';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async getTenant(id: string) {
    // First check if the tenant is already loaded in the list
    const cachedTenant = this.tenants.find(t => t.id === id);
    if (cachedTenant) {
      return cachedTenant;
    }

    // Check user permissions and tenant ID
    const currentUser = authStore.user;
    const effectiveTenantId = authStore.effectiveTenantId;

    // If requesting own tenant ID, or user is not root admin, use current tenant endpoint
    // This prevents "Insufficient permissions" error for tenant admins
    if (id === effectiveTenantId || currentUser?.role !== 'root') {
      try {
        const tenant = await tenantApi.getCurrentTenant();
        return tenant;
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to get current tenant';
        throw error;
      }
    }

    // Root admin accessing other tenant - use tenant-specific endpoint
    try {
      const tenant = await tenantApi.getTenant(id);
      return tenant;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to get tenant';
      throw error;
    }
  }

  async updateTenantSettings(id: string, settings: any) {
    this.isLoading = true;
    this.error = null;
    try {
      // For tenant admin, use the /admin/tenant endpoint
      const isCurrentTenant = this.selectedTenant?.id === id || !this.selectedTenant;

      // Send only the settings to the backend (backend handles merging)
      const tenantUpdate = { settings };

      // Debug: Log the data being sent
      console.log('Updating tenant settings:', {
        tenantId: id,
        isCurrentTenant,
        settingsPayload: tenantUpdate
      });

      const updatedTenant = isCurrentTenant
        ? await tenantApi.updateCurrentTenant(tenantUpdate)
        : await tenantApi.updateTenant(id, tenantUpdate);

      runInAction(() => {
        const index = this.tenants.findIndex(t => t.id === id);
        if (index !== -1) {
          this.tenants[index] = updatedTenant;
        }
        if (this.selectedTenant?.id === id || isCurrentTenant) {
          this.selectedTenant = updatedTenant;
        }
        this.isLoading = false;
      });
      return updatedTenant;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to update tenant settings';
        this.isLoading = false;
      });
      throw error;
    }
  }

  clearError() {
    this.error = null;
  }

  clearSelection() {
    this.selectedTenant = null;
    this.selectedLLMConfig = null;
    this.selectedNotificationConfig = null;
    this.selectedMonitoringConfig = null;
  }
}

export const tenantStore = new TenantStore();